<template>
  <v-container fluid class="requests-page">
    <div class="page-header">
      <div>
        <div class="eyebrow">Depósito y ventas</div>
        <h1>Mis solicitudes de reposición</h1>
        <p>Seguimiento de artículos pedidos a Compras para reponer stock.</p>
      </div>
      <v-btn color="primary" :to="{ name: 'internal-order-create' }">
        <v-icon left>mdi-plus</v-icon>
        Nueva solicitud
      </v-btn>
    </div>

    <v-card flat class="table-panel">
      <v-card-text>
        <v-row dense align="center">
          <v-col cols="12" md="4">
            <v-select
              v-model="filters.status"
              :items="statusFilters"
              item-text="text"
              item-value="value"
              label="Estado"
              outlined
              dense
              hide-details
              @change="reload"
            />
          </v-col>
          <v-col cols="12" md="4">
            <v-text-field
              v-model="filters.search"
              label="Filtrar proveedor"
              outlined
              dense
              hide-details
              clearable
              prepend-inner-icon="mdi-magnify"
            />
          </v-col>
          <v-col cols="12" md="4" class="text-md-right">
            <v-btn outlined color="primary" :loading="loading" @click="loadOrders">
              <v-icon left>mdi-refresh</v-icon>
              Actualizar
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>

      <v-data-table
        :headers="headers"
        :items="filteredOrders"
        :loading="loading"
        :options.sync="options"
        :server-items-length="totalItems"
        :footer-props="{ 'items-per-page-options': [10, 25, 50] }"
        class="requests-table"
        no-data-text="No hay solicitudes para mostrar"
        loading-text="Cargando solicitudes..."
      >
        <template v-slot:item.provider="{ item }">
          <div class="font-weight-bold">{{ item.provider?.name || "-" }}</div>
          <div class="text-caption grey--text">{{ item.provider?.code || "" }}</div>
        </template>
        <template v-slot:item.orderDate="{ item }">
          {{ formatDate(item.orderDate) }}
        </template>
        <template v-slot:item.status="{ item }">
          <v-chip small :color="getStatusColor(item.status)" text-color="white">
            {{ getStatusLabel(item.status) }}
          </v-chip>
        </template>
        <template v-slot:item.items="{ item }">
          <div class="items-summary">
            <v-icon small color="primary">mdi-package-variant</v-icon>
            {{ item.items?.length || 0 }} ítems
          </div>
        </template>
        <template v-slot:item.actions="{ item }">
          <v-btn icon small color="primary" @click="viewOrder(item.id)">
            <v-icon small>mdi-eye-outline</v-icon>
          </v-btn>
          <v-btn icon small color="primary" class="mx-1" v-if="canEdit(item)" @click="editOrder(item.id)">
            <v-icon small>mdi-pencil-outline</v-icon>
          </v-btn>
          <v-btn icon small color="error" v-if="canDelete(item)" @click="confirmDelete(item)">
            <v-icon small>mdi-delete-outline</v-icon>
          </v-btn>
        </template>
      </v-data-table>
    </v-card>

    <v-dialog v-model="deleteDialog" max-width="430">
      <v-card v-if="orderToDelete">
        <v-card-title class="error white--text">
          <v-icon left color="white">mdi-delete-outline</v-icon>
          Eliminar solicitud
        </v-card-title>
        <v-card-text class="pt-4">
          Se eliminará la solicitud para <strong>{{ orderToDelete.provider?.name }}</strong>. Esta acción no genera movimiento de Compras.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text :disabled="deleting" @click="deleteDialog = false">Cancelar</v-btn>
          <v-btn color="error" :loading="deleting" @click="deleteOrder">Eliminar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar
      v-model="snackbar.show"
      :timeout="4000"
      top
      right
      :class="[`module-toast`, `module-toast--${snackbar.color}`]"
      content-class="white--text font-weight-bold"
    >
      {{ snackbar.message }}
      <template v-slot:action>
        <v-btn text color="white" @click="snackbar.show = false">Cerrar</v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import { listOrders, deleteOrder, generateIdempotencyKey } from "@/services/internalOrders";
import { mapGetters } from "vuex";

export default {
  name: "InternalOrdersList",
  data() {
    return {
      loading: false,
      orders: [],
      totalItems: 0,
      options: { page: 1, itemsPerPage: 10 },
      filters: { status: "", search: "" },
      statusFilters: [
        { text: "Todos", value: "" },
        { text: "Pendientes", value: "pending" },
        { text: "Aprobados / OC creada", value: "completed" },
        { text: "Rechazados", value: "cancelled" },
      ],
      headers: [
        { text: "Fecha", value: "orderDate", width: 120 },
        { text: "Proveedor", value: "provider" },
        { text: "Estado", value: "status", width: 170 },
        { text: "Artículos", value: "items", sortable: false, width: 120 },
        { text: "Acciones", value: "actions", sortable: false, align: "center", width: 130 },
      ],
      deleteDialog: false,
      deleting: false,
      orderToDelete: null,
      snackbar: { show: false, message: "", color: "success" },
    };
  },
  computed: {
    ...mapGetters("auth", ["user", "hasPermission"]),
    filteredOrders() {
      const term = String(this.filters.search || "").trim().toLowerCase();
      if (!term) return this.orders;
      return this.orders.filter((order) => {
        return [order.provider?.name, order.provider?.code, order.notes]
          .join(" ")
          .toLowerCase()
          .includes(term);
      });
    },
  },
  watch: {
    options: {
      handler() {
        this.loadOrders();
      },
      deep: true,
    },
  },
  mounted() {
    this.loadOrders();
  },
  methods: {
    reload() {
      this.options.page = 1;
      this.loadOrders();
    },
    async loadOrders() {
      this.loading = true;
      try {
        const { page, itemsPerPage } = this.options;
        const offset = (page - 1) * itemsPerPage;
        const filters = { limit: itemsPerPage, offset };
        if (this.filters.status) filters.status = this.filters.status;
        const response = await listOrders(filters);
        if (response.ok) {
          this.orders = response.data.orders || [];
          this.totalItems = response.data.total || 0;
        } else {
          this.showError(response.error || "Error al cargar solicitudes");
        }
      } catch (error) {
        this.showError("Error al cargar solicitudes");
      } finally {
        this.loading = false;
      }
    },
    getStatusColor(status) {
      return { pending: "warning", completed: "success", cancelled: "error" }[status] || "grey";
    },
    getStatusLabel(status) {
      return { pending: "Pendiente de Compras", completed: "OC creada", cancelled: "Rechazado" }[status] || status;
    },
    formatDate(dateStr) {
      return dateStr ? new Date(dateStr).toLocaleDateString("es-AR", { day: "2-digit", month: "2-digit", year: "numeric" }) : "-";
    },
    viewOrder(orderId) {
      this.$router.push({ name: "internal-order-detail", params: { id: orderId } });
    },
    editOrder(orderId) {
      this.$router.push({ name: "internal-order-edit", params: { id: orderId } });
    },
    canEdit(order) {
      return order.status === "pending" && (order.createdBy === this.user?.email || order.createdBy === this.user?.username || this.hasPermission("internal_orders.manage"));
    },
    canDelete(order) {
      return order.status === "pending" && (order.createdBy === this.user?.email || order.createdBy === this.user?.username || this.hasPermission("internal_orders.manage"));
    },
    confirmDelete(order) {
      this.orderToDelete = order;
      this.deleteDialog = true;
    },
    async deleteOrder() {
      this.deleting = true;
      try {
        const response = await deleteOrder(this.orderToDelete.id, generateIdempotencyKey("delete"));
        if (response.ok) {
          this.showSuccess("Solicitud eliminada");
          this.deleteDialog = false;
          this.loadOrders();
        } else {
          this.showError(response.error || "Error al eliminar");
        }
      } catch (error) {
        this.showError("Error al eliminar la solicitud");
      } finally {
        this.deleting = false;
      }
    },
    showSuccess(message) {
      this.snackbar = { show: true, message, color: "success" };
    },
    showError(message) {
      this.snackbar = { show: true, message, color: "error" };
    },
  },
};
</script>

<style scoped>
.requests-page {
  --module-primary: var(--nav-link-active-color, #223b6a);
  --module-action: var(--accent-primary, #3b82f6);
  --module-line: var(--border-color, rgba(26, 35, 51, 0.1));
  --module-muted: var(--text-tertiary, #64748b);
  background: transparent;
  min-height: calc(100vh - 64px);
  padding-top: 12px;
}

.page-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 14px;
}

.page-header h1 {
  margin: 0;
  color: var(--module-primary);
  font-size: 22px;
  font-weight: 700;
  line-height: 1.25;
}

.page-header p {
  margin: 4px 0 0;
  color: var(--module-muted);
  font-size: 14px;
}

.eyebrow {
  color: var(--module-action);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: .04em;
  text-transform: uppercase;
}

.table-panel {
  border: 1px solid var(--module-line);
  border-radius: 8px;
  background: var(--bg-secondary, #fff) !important;
}

.requests-table th {
  color: var(--text-secondary, #475569) !important;
  font-size: 11px !important;
  font-weight: 700 !important;
  text-transform: uppercase;
}

.requests-table td,
.table-panel >>> .v-label,
.table-panel >>> input,
.table-panel >>> .v-select__selection {
  font-size: 14px;
}

.items-summary {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: var(--text-primary, #1a2333);
  font-weight: 600;
}

.module-toast .v-snack__wrapper {
  border-radius: 8px !important;
  box-shadow: 0 12px 28px rgba(15, 23, 42, 0.24) !important;
}

.module-toast--success .v-snack__wrapper {
  background: #166534 !important;
}

.module-toast--error .v-snack__wrapper {
  background: #991b1b !important;
}

@media (max-width: 720px) {
  .page-header {
    flex-direction: column;
  }

  .page-header .v-btn {
    width: 100%;
  }
}
</style>
